<template>
  <div class="flex w-full grow flex-col gap-4 overflow-y-auto p-4">
    <ui-event-modal-header :event="relatedEvent" @close="mapStore.closePopup()" />

    <div class="flex w-full grow flex-col gap-y-2">
      <ui-tooltip-tile
        v-for="hazard in [
          ...relatedHazards.warnings,
          ...relatedHazards.centres,
          ...relatedHazards.outagesAndClosures,
          ...relatedHazards.incidents,
          ...relatedHazards.naturalHazards,
        ]"
        :key="hazard.id"
        :background-color="hazard.backgroundColor"
        :leading-color="hazard.leadingColor"
        :title="hazard.title"
        :description="hazard.description"
        :published-at="hazard.publishedAt"
        :icon="hazard.icon"
        :selected="hazard.id === mapStore.selectedPopupHazard?.hazardId"
        @icon:clicked="
          toggleSelectedHazard({
            hazardId: hazard.id,
            eventId: hazard.eventId!,
            hazardType: hazard.entityType,
          })
        "
      />
    </div>

    <ui-map-popup-footer
      :left-text="t('lSeWusNweldd_c1g_1aJb')"
      :left-callback="zoomToFeatures"
      :right-text="t('OxJBRsY6Yfqi--lYTXRqd')"
      :right-callback="() => toggleSelectedHazard({ hazardId, eventId, hazardType })"
    />
  </div>
</template>

<script setup lang="ts">
import { EventEntity, HazardType, SelectedHazard, SelectedHazardViewType, useHazardsStore, useMapStore } from '@/features/hazards';
import { i18n } from '@/locales';
import { computed, watch } from 'vue';

const props = defineProps<{
  hazardType: HazardType;
  hazardId: string;
  eventId: string;
}>();

const { t } = i18n.global;
const mapStore = useMapStore();

const hazardsStore = useHazardsStore();

// Get all the hazards related to the event.
const relatedHazards = computed(() => {
  return hazardsStore.getHazardsByEvent(props.eventId);
});

const relatedEvent = computed<EventEntity | undefined>(() => {
  return hazardsStore.hazards.events.find((event) => event.id === props.eventId);
});

const hazard = computed(() => {
  return hazardsStore.hazards[props.hazardType].find((h) => h.id === props.hazardId);
});

const pointFeatures = computed(() => {
  const incident = relatedHazards.value?.incidents.find((incident) => incident.id === mapStore.selectedPopupHazard?.hazardId);
  const warning = relatedHazards.value?.warnings.find((warning) => warning.id === mapStore.selectedPopupHazard?.hazardId);
  const outageOrClosure = relatedHazards.value?.outagesAndClosures.find(
    (outageOrClosure) => outageOrClosure.id === mapStore.selectedPopupHazard?.hazardId
  );
  const centre = relatedHazards.value?.centres.find((centre) => centre.id === mapStore.selectedPopupHazard?.hazardId);

  return (warning ?? incident ?? outageOrClosure ?? centre)?.pointFeatures;
});

watch(hazard, (incomingHazard, previousHazard) => {
  const selectedHazardId = mapStore.selectedPopupHazard?.hazardId || hazardsStore.selectedHazard?.hazardId;
  if (incomingHazard || !previousHazard || selectedHazardId !== previousHazard?.id) return;

  mapStore.closePopup();
});

const toggleSelectedHazard = (hazard: SelectedHazard) => {
  hazardsStore.toggleSelectHazard({ ...hazard, viewType: SelectedHazardViewType.Modal });
};

const zoomToFeatures = () => {
  mapStore.flyToFeatures(pointFeatures.value);
};
</script>
